import Table, { ColumnsType } from 'antd/lib/table';
import { TaxInvoice } from '../../modules/invoices';
import { corporateNumberFormatter, priceToString } from '../../util/utils';
import moment from 'moment';
import styled from 'styled-components';
import { styledTable } from '../styled/mixin';
import {
  Button,
  Col,
  Row,
  Space,
  Tag as ANTDTag,
  Alert,
  Tooltip,
  Dropdown,
  MenuProps,
} from 'antd';
import Title from 'antd/lib/typography/Title';
import {
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';

const StyledDiv = styled.div`
  ${styledTable}
`;

interface TaxInvoiceListComponentProps {
  data: TaxInvoice[];
  importTaxInvoices: () => void;
  openMappingInvoiceModal: (taxInvoiceId: number) => void;
  excludeTaxInvoice: (taxInvoiceIds: number[]) => void;
  moveToCreatePage: () => void;
  setTaxInvoiceId: (taxInvoiceId: number) => void;
}

function TaxInvoiceListComponent({
  data,
  importTaxInvoices,
  openMappingInvoiceModal,
  excludeTaxInvoice,
  moveToCreatePage,
  setTaxInvoiceId,
}: TaxInvoiceListComponentProps) {
  const [visibleExcludeTaxInvoice, setVisibleExcludeTaxInvoice] =
    useState(false);
  const [selectedValues, setSelectedValues] = useState<any>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);

  useEffect(() => {
    setSelectedValues([]);
  }, []);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedValues([...selectedRowKeys]);
      setSelectedRows([...selectedRows]);
    },
    selectedRowKeys: selectedValues,
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: '설정',
    },
    {
      key: '2',
      label: '상세',
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = async (e: any) => {
    if (e.keyPath.length === 0) return;
    const selectedTaxInvoiceId = parseInt(e.keyPath[0]);
    if (e.key === '1') {
      openMappingInvoiceModal(selectedTaxInvoiceId);
    } else if (e.key === '2') {
      setTaxInvoiceId(selectedTaxInvoiceId);
    }
  };

  const columns: ColumnsType<TaxInvoice> = [
    {
      title: '작성일자',
      dataIndex: 'reportingDate',
      width: '10%',
      sorter: (a: TaxInvoice, b: TaxInvoice) =>
        a.reportingDate.localeCompare(b.reportingDate),
      render: (text: any, record: any, index: any) =>
        moment(record.reportingDate).format('YYYY-MM-DD'),
    },
    {
      title: '사업자 등록번호',
      dataIndex: 'supplierRegNumber',
      width: '10%',
      render: (value: any, record: any, index: number) => (
        <span>
          {value === undefined ? null : corporateNumberFormatter(value)}
        </span>
      ),
    },
    {
      title: '상호',
      dataIndex: 'supplierCompanyName',
      ellipsis: true,
      width: '13%',
    },
    {
      title: '대표명',
      dataIndex: 'supplierName',
      ellipsis: true,
      width: '5%',
    },
    {
      title: '종류',
      width: '8%',
      render: (value: any, record: any, index: any) =>
        record.type === '0' ? (
          <ANTDTag color={'purple'}>세금계산서</ANTDTag>
        ) : (
          <ANTDTag color={'cyan'}>현금영수증</ANTDTag>
        ),
    },
    {
      title: '공급가액(원)',
      width: '10%',
      align: 'right',
      render: (value: any, record: any, index: any) =>
        priceToString(record.supplyValue),
    },
    {
      title: '부가세(원)',
      width: '9%',
      align: 'right',
      render: (value: any, record: any, index: any) =>
        priceToString(value.taxAmt),
    },
    {
      title: '합계금액(원)',
      width: '10%',
      align: 'right',
      render: (value: any, record: any, index: any) =>
        priceToString(record.supplyValue + record.taxAmt),
    },
    {
      title: '대표품목',
      dataIndex: 'repItems',
      ellipsis: true,
      width: '13%',
    },
    {
      title: '증빙확인',
      width: '7%',
      align: 'center',
      render: (value: any, record: any, index: any) => {
        const _totalConnectedInvoicePrice = record.invoices.reduce(
          (acc: number, cur: any) => acc + cur.price,
          0,
        );
        const _taxInvoicePrice = record.supplyValue + record.taxAmt;
        let result = '';
        let color = '';
        if (_totalConnectedInvoicePrice === 0) {
          result = '아니오';
          color = 'red';
        } else if (_totalConnectedInvoicePrice === _taxInvoicePrice) {
          result = '예';
          color = 'blue';
        } else if (_totalConnectedInvoicePrice < _taxInvoicePrice) {
          result = '진행중';
          color = 'green';
        } else {
          result = '분할';
          color = 'olive';
        }
        return result === '아니오' ? (
          <ANTDTag color={color}>{result}</ANTDTag>
        ) : (
          <Tooltip
            placement="topLeft"
            title={
              <>
                <p>청구내역</p>
                <ul>
                  {record.invoices.map((item: any, index: any) => (
                    <li key={index}>
                      {item.billingDate} | {item.project.name} |{' '}
                      {priceToString(item.price)}원
                    </li>
                  ))}
                </ul>
                {record.description && (
                  <>
                    <p>비고</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                      {record.description}
                    </p>
                  </>
                )}
              </>
            }
          >
            <ANTDTag color={color}>{result}</ANTDTag>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'action',
      dataIndex: '',
      align: 'center',
      width: '5%',
      render: (text, record, index) => {
        if (record.isFetched) {
          return (
            <Button
              size="small"
              onClick={() => {
                openMappingInvoiceModal(record.id);
              }}
            >
              설정
            </Button>
          );
        } else {
          return (
            <Row justify={'center'}>
              <Col>
                {/* <Dropdown.Button
                  menu={{ items, onClick: handleMenuClick }}
                  placement="bottomLeft"
                  arrow
                  size="small"
                ></Dropdown.Button> */}
                <Dropdown
                  menu={{
                    items,
                    onClick: (event) => {
                      if (handleMenuClick) {
                        event.keyPath = [record.id + ''];
                        handleMenuClick(event);
                      }
                    },
                  }}
                  placement="bottomLeft"
                >
                  <Button size="small">
                    <Space>
                      <DownOutlined rev={undefined} />
                      {/* <MoreOutlined rev={undefined} /> */}
                    </Space>
                  </Button>
                </Dropdown>
              </Col>
            </Row>
          );
        }
      },
    },
  ];

  return (
    <StyledDiv>
      {visibleExcludeTaxInvoice && (
        <Row style={{ marginBottom: '10px' }}>
          <Col flex={1}>
            <Alert
              message={`매입증빙 왼쪽의 체크박스를 선택 후 취소/재발행 건을 정리하세요.`}
              type="info"
              closable={false}
              action={
                <Space>
                  <Button
                    size="small"
                    type="ghost"
                    disabled={
                      selectedRows.length !== 2 ||
                      selectedRows[0].supplyValue +
                        selectedRows[0].taxAmt +
                        selectedRows[1].supplyValue +
                        selectedRows[1].taxAmt !==
                        0
                    }
                    onClick={() => {
                      excludeTaxInvoice(selectedValues);
                      setVisibleExcludeTaxInvoice(false);
                    }}
                  >
                    정리하기
                  </Button>
                  <Button
                    size="small"
                    type="ghost"
                    onClick={() => {
                      setVisibleExcludeTaxInvoice(false);
                      setSelectedRows([]);
                      setSelectedValues([]);
                    }}
                  >
                    창 닫기
                  </Button>
                </Space>
              }
            />
          </Col>
        </Row>
      )}
      <Row justify="space-between">
        <Col>
          <Title level={4}>매입증빙</Title>
        </Col>
        <Col>
          <Space>
            <Button
              size="small"
              onClick={() => importTaxInvoices()}
              icon={<SyncOutlined rev={undefined} />}
            >
              홈택스정보 가져오기
            </Button>
            <Button
              size="small"
              onClick={() => setVisibleExcludeTaxInvoice(true)}
              icon={<EditOutlined rev={undefined} />}
            >
              취소(재발행) 건 정리
            </Button>
            <Button
              size="small"
              onClick={() => {
                moveToCreatePage();
              }}
              icon={<PlusOutlined rev={undefined} />}
            >
              직접등록하기
            </Button>
          </Space>
        </Col>
      </Row>

      {data && (
        <Table<TaxInvoice>
          rowKey="id"
          columns={columns}
          dataSource={data}
          rowSelection={
            visibleExcludeTaxInvoice
              ? { ...rowSelection, checkStrictly: false }
              : undefined
          }
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
            position: ['bottomCenter'],
          }}
          locale={{
            triggerDesc: '내림차순으로 정렬하려면 클릭하세요.',
            triggerAsc: '오름차순으로 정렬하려면 클릭하세요.',
            cancelSort: '정렬을 취소하려면 클릭하세요.',
          }}
          summary={(pageData) => {
            let totalSupplyValue = 0;
            let totalTaxAmt = 0;

            pageData.forEach(({ supplyValue, taxAmt }) => {
              totalSupplyValue += supplyValue;
              totalTaxAmt += taxAmt;
            });
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}></Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    Total:
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    {priceToString(totalSupplyValue)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    {priceToString(totalTaxAmt)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} align="right">
                    {priceToString(totalTaxAmt + totalSupplyValue)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={12}></Table.Summary.Cell>
                  <Table.Summary.Cell index={13}></Table.Summary.Cell>
                  <Table.Summary.Cell index={14}></Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
        />
      )}
    </StyledDiv>
  );
}
export default TaxInvoiceListComponent;
